import React, { memo } from 'react';

import { TeamNanoID } from '../../../../teams/teamsTypes';
import { IconsEnum } from '../../../../../assets/icons/types';

import { ThreeDStockSubHeaderSelectedButton } from './components/ThreeDStockSubHeaderSelectedButton';

import { PureIconButtonHelper } from '../../../../../helpers/buttons/PureIconButtonHelper';
import { CheckPermissions } from '../../../../../helpers/CheckPermissions';
import { NextLinkHelper } from '../../../../../helpers/links/NextLinkHelper';
import { Translate } from '../../../../../helpers/Translate';

import { StockPath } from '../../../StockPath';
import { TeamPath } from '../../../../teams/TeamPath';
import { StockPermissions, StockTabs } from '../../../stockConstants';

import {
  lifestylesKeys,
  materialsKeys,
  productsKeys,
  words
} from '../../../../../locales/keys';

const ThreeDStockSubHeaderLinks = (
  isMyLibrary: boolean,
  companyNanoId: TeamNanoID
) => [
  {
    tab: StockTabs.PRODUCTS,
    defaultHref: StockPath.newProducts(),
    myLybHref: isMyLibrary ? TeamPath.currentCompanyNewLibraryProducts() : null,
    companyHref: companyNanoId
      ? TeamPath.companyNewLibraryProducts(companyNanoId)
      : null,
    i18nText: productsKeys.plural,
    action: StockPermissions.READ_THREE_D_STOCK_PRODUCTS_LINK
  },
  {
    tab: StockTabs.LIFESTYLES,
    defaultHref: StockPath.newLifestyles(),
    myLybHref: isMyLibrary
      ? TeamPath.currentCompanyNewLibraryLifestyles()
      : null,
    companyHref: companyNanoId
      ? TeamPath.companyNewLibraryLifestyles(companyNanoId)
      : null,
    i18nText: lifestylesKeys.plural,
    action: StockPermissions.READ_THREE_D_STOCK_LIFESTYLES_LINK
  },
  {
    tab: StockTabs.MATERIALS,
    defaultHref: StockPath.newMaterials(),
    myLybHref: isMyLibrary
      ? TeamPath.currentCompanyNewLibraryMaterials()
      : null,
    companyHref: companyNanoId
      ? TeamPath.companyNewLibraryMaterials(companyNanoId)
      : null,
    i18nText: materialsKeys.plural,
    action: StockPermissions.READ_THREE_D_STOCK_MATERIALS_LINK
  }
];

interface ThreeDStockSubHeaderProps {
  tab?: StockTabs;
  onAllClick?: () => void;
  selectedItemsCount?: number;
  onSelectedItemsSidebarOpen?: () => void;
  isMyLibrary?: boolean;
  companyNanoId?: TeamNanoID;
}

function ThreeDStockSubHeader({
  tab,
  onAllClick,
  onSelectedItemsSidebarOpen,
  selectedItemsCount,
  companyNanoId,
  isMyLibrary
}: ThreeDStockSubHeaderProps) {
  const links = ThreeDStockSubHeaderLinks(isMyLibrary, companyNanoId);

  return (
    <div
      id="3d-stock-submenu-container"
      className="px-4 py-2 dark:bg-gray-800 border-b dark:border-gray-700"
    >
      <div id="3d-stock-submenu" className="flex w-full">
        <PureIconButtonHelper
          className="py-1.5 pl-1.5 pr-3 space-x-1 rounded-md inline-flex items-center whitespace-nowrap text-sm font-medium leading-5 focus:ring-base hover:text-gray-950 dark:hover:text-white hover:bg-gray-200 dark:hover:bg-gray-700 focus:ring-offset-0 transition-colors duration-150 ease-out"
          i18nTextClassName="block"
          icon={IconsEnum.MENU_SOLID}
          i18nText={words.all}
          onClick={onAllClick}
        />

        <CheckPermissions actions={links.map((link) => link.action)}>
          <div className="px-2">
            <div className="border-l dark:border-gray-700 h-full" />
          </div>
        </CheckPermissions>

        <div className="hidden sm:flex">
          {links.map((link, index) => (
            <CheckPermissions action={link.action} key={index}>
              <NextLinkHelper
                className="py-1.5 pl-3 pr-3 rounded-md inline-flex items-center whitespace-nowrap text-sm font-medium leading-5 focus:ring-base hover:text-gray-950 dark:hover:text-white hover:bg-gray-200 dark:hover:bg-gray-700 focus:ring-offset-0 relative transition-colors duration-150 ease-out"
                href={link.myLybHref || link.companyHref || link.defaultHref}
                dataGa={`link-${link.tab}`}
              >
                <Translate id={link.i18nText} />
                {link.tab === tab && (
                  <span className="-bottom-2 absolute bg-blue-500 h-0.5 left-0 w-full" />
                )}
              </NextLinkHelper>
            </CheckPermissions>
          ))}

          <CheckPermissions actions={links.map((link) => link.action)}>
            <div className="px-2">
              <div className="border-l dark:border-gray-700 h-full" />
            </div>
          </CheckPermissions>
        </div>

        <div className="px-2 ml-auto">
          <div className="border-l dark:border-gray-700 h-full" />
        </div>

        {onSelectedItemsSidebarOpen && (
          <ThreeDStockSubHeaderSelectedButton
            onSelectedItemsSidebarOpen={onSelectedItemsSidebarOpen}
            selectedItemsCount={selectedItemsCount}
          />
        )}
      </div>
    </div>
  );
}

export default memo<ThreeDStockSubHeaderProps>(ThreeDStockSubHeader);
